import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Globe, Cookie } from "lucide-react";
import { useCart } from "@/App";
import { useLanguage } from "@/LanguageContext";
import { Button } from "@/components/ui/button";

export const Navbar = () => {
  const { cart } = useCart();
  const { lang, toggleLang, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const location = useLocation();

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const navLinks = [
    { path: "/menu", label: t("nav_menu") },
    { path: "/booking", label: t("nav_booking") },
    { path: "/catering", label: t("nav_catering") },
    { path: "/gift-cards", label: t("nav_giftcard") },
    { path: "/about", label: t("nav_about") },
    { path: "/contact", label: t("nav_contact") },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      className="sticky top-0 z-50 shadow-lg"
      style={{ 
        background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)',
        borderBottom: '3px solid #d4af37'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center space-x-3" data-testid="nav-logo">
            <span className="text-3xl font-script" style={{ color: '#d4af37' }}>
              Masala Indisk
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                  isActive(link.path) 
                    ? 'bg-amber-600/20' 
                    : 'hover:bg-amber-600/10'
                }`}
                style={{ 
                  color: isActive(link.path) ? '#d4af37' : '#f5e6d3'
                }}
                data-testid={`nav-link-${link.path.replace('/', '')}`}
              >
                {link.label}
              </Link>
            ))}

            {/* Language Toggle */}
            <button
              onClick={toggleLang}
              className="ml-2 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all hover:scale-105"
              style={{ 
                background: 'rgba(212, 175, 55, 0.15)',
                border: '1.5px solid #d4af37',
                color: '#d4af37'
              }}
              data-testid="language-toggle"
              title={lang === "no" ? "Switch to English" : "Bytt til Norsk"}
            >
              <Globe className="h-3.5 w-3.5" />
              {lang === "no" ? "EN" : "NO"}
            </button>

            <Link to="/order" data-testid="nav-cart-button" className="ml-2">
              <Button
                size="sm"
                className="relative px-4 py-2 rounded-full"
                style={{ background: '#d4af37', color: '#2d1810' }}
              >
                <ShoppingBag className="h-5 w-5" />
                {cartCount > 0 && (
                  <span 
                    className="absolute -top-2 -right-2 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold"
                    style={{ background: '#721111' }}
                    data-testid="cart-count"
                  >
                    {cartCount}
                  </span>
                )}
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={toggleLang}
              className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold"
              style={{ 
                border: '1.5px solid #d4af37',
                color: '#d4af37'
              }}
              data-testid="language-toggle-mobile"
            >
              <Globe className="h-3.5 w-3.5" />
              {lang === "no" ? "EN" : "NO"}
            </button>
            <button
              onClick={() => { localStorage.removeItem("masala_cookie_consent"); window.location.reload(); }}
              className="flex items-center px-2 py-1 rounded-full"
              style={{ border: '1px solid rgba(212, 175, 55, 0.4)', color: '#d4af37' }}
              data-testid="cookie-preferences-btn-mobile"
              title="Informasjonskapsler"
            >
              <Cookie className="h-3.5 w-3.5" />
            </button>
            <Link to="/order" data-testid="nav-cart-button-mobile">
              <Button
                size="sm"
                className="relative px-3 py-2 rounded-full"
                style={{ background: '#d4af37', color: '#2d1810' }}
              >
                <ShoppingBag className="h-5 w-5" />
                {cartCount > 0 && (
                  <span 
                    className="absolute -top-2 -right-2 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold"
                    style={{ background: '#721111' }}
                  >
                    {cartCount}
                  </span>
                )}
              </Button>
            </Link>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
              style={{ color: '#d4af37' }}
            >
              {mobileMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div 
            className="lg:hidden py-4 space-y-1 border-t"
            style={{ borderColor: '#d4af37' }}
            data-testid="mobile-menu"
          >
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 px-4 rounded-lg font-medium transition-colors ${
                  isActive(link.path) 
                    ? 'bg-amber-600/20' 
                    : 'hover:bg-amber-600/10'
                }`}
                style={{ 
                  color: isActive(link.path) ? '#d4af37' : '#f5e6d3'
                }}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};
